import { useOpportunityStatementsStatusesQuery } from '~/composables/opportunities/useOpportunityStatementsStatusesQuery';
import type { APIError } from '~/interfaces/api/main';
import type { BankAccountStatementStatus } from '~/interfaces/opportunities/bankAccounts/statements/statuses/main';

export const useOpportunityStatementsStatusesStore = defineStore('OpportunityStatementsStatusesStore', () => {
  const requestedOpportunityId = ref<string | null>(null);
  const requestedParams = ref<string | null>(null);
  const enableQuery = ref<boolean>(false);

  const {
    query,
    statuses,
    alert,
    markDocumentAsExtracting: markInQuery,
  } = useOpportunityStatementsStatusesQuery(
    computed(() => requestedOpportunityId.value),
    computed(() => requestedParams.value),
    { enabled: computed(() => enableQuery.value && Boolean(requestedOpportunityId.value)) },
  );

  const opportunityStatementsStatuses = statuses;
  const opportunityStatementsStatusesAlert = alert;
  const loading = computed<boolean>(() => Boolean(query?.isFetching?.value || query?.isPending?.value));
  const error = computed<APIError[] | null>(() => (query?.error?.value as APIError[] | null) ?? null);

  const opportunityIncompleteStatementsStatuses = computed(() => {
    if (!opportunityStatementsStatuses.value.length) { return null; }
    return opportunityStatementsStatuses.value.filter((s: BankAccountStatementStatus) => s.status !== 'validated');
  });

  async function fetchOpportunityStatementsStatuses (opportunityId: string, params: string | null = null): Promise<void> {
    requestedOpportunityId.value = opportunityId;
    requestedParams.value = params;
    enableQuery.value = true;
    await query.refetch();
  }

  function markDocumentAsExtracting (documentId: number): void {
    markInQuery(documentId);
  }

  return {
    opportunityStatementsStatuses,
    opportunityIncompleteStatementsStatuses,
    opportunityStatementsStatusesAlert,
    loading,
    error,
    fetchOpportunityStatementsStatuses,
    markDocumentAsExtracting,
  };
});
